// Resilient ICE-server provider (field 2026-07-19): the TURN-credentials
// fetch runs at dial/accept time — exactly when a doze-woken phone's radio is
// still warming up — and a single failure used to silently yield ZERO ice
// servers, leaving the call to attempt P2P with host candidates only. That is
// the "accept → Connecting… → Could not connect, but calling back immediately
// works" pattern: the retry finds a warm network.
//
// Strategy (Connection > Latency): retry the fetch with short backoff, cache
// a success for its advertised TTL, and fall back to unexpired cached
// credentials when every attempt fails. An empty list is returned only when
// there is truly nothing — and it is never cached.

import type { IceServer } from '@merkury/protocol';

export interface TurnCredentialsLike {
  iceServers: IceServer[];
  ttlSeconds: number;
}

export interface IceServerProviderDeps {
  fetchCredentials: () => Promise<TurnCredentialsLike>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/** Attempt delays: immediate, then two short backoffs (~0.9 s worst case). */
const RETRY_DELAYS_MS = [0, 300, 600];

/** Serve cached credentials for at most this fraction of their TTL. */
const CACHE_TTL_FRACTION = 0.8;

export function createIceServerProvider(deps: IceServerProviderDeps): () => Promise<IceServer[]> {
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  let cache: { servers: IceServer[]; expiresAt: number } | null = null;

  return async () => {
    for (const delay of RETRY_DELAYS_MS) {
      if (delay > 0) await sleep(delay);
      try {
        const r = await deps.fetchCredentials();
        if (r.iceServers.length > 0) {
          cache = {
            servers: r.iceServers,
            expiresAt: now() + Math.max(0, r.ttlSeconds) * 1000 * CACHE_TTL_FRACTION,
          };
          return r.iceServers;
        }
      } catch {
        // Transient (radio warming, token refresh race): retry, then cache.
      }
    }
    if (cache && now() < cache.expiresAt) return cache.servers;
    return [];
  };
}

/**
 * STUN servers derived from the SIGNED endpoint list, for when the API cannot
 * be reached at all.
 *
 * A TURN allocation needs credentials, and those come from `GET /turn/credentials`
 * — so when the API is unreachable the provider above correctly yields nothing
 * and the peer connection was left with host candidates only. That means no NAT
 * traversal: a call between two phones on mobile networks simply cannot connect,
 * which is precisely the situation this app is built for.
 *
 * A STUN binding request needs NO credentials, so the same hosts can still tell
 * us our server-reflexive address and let ICE punch through most NATs. The URLs
 * come from the signed list the client already trusts and persists (docs/04 §2),
 * so this adds no third party — deliberately not a public STUN server, which
 * would leak every call setup to an outsider and be blocked first under the
 * censorship this design assumes.
 *
 * Only `turn:` entries are used, and only their host:port. Three reasons not to
 * touch the rest:
 *  - `stuns:` (STUN over TLS) is not reliably supported by browser WebRTC, and
 *    an ICE server URL the implementation rejects throws SyntaxError from the
 *    RTCPeerConnection CONSTRUCTOR — that would fail call setup outright, which
 *    is far worse than the empty list this fallback exists to replace.
 *  - a TURN TLS port serves TLS only, so a plaintext STUN binding sent there
 *    could not be answered anyway.
 *  - `wss:` TURN entries have no STUN meaning at all.
 * A `?transport=` query is dropped because STUN URIs do not carry one.
 */
export function stunFallbackFrom(list: {
  turn?: readonly { url: string }[] | undefined;
}): IceServer[] {
  const urls: string[] = [];
  for (const entry of list.turn ?? []) {
    const bare = entry.url.split('?')[0] ?? '';
    if (!bare.startsWith('turn:')) continue;
    const stun = `stun:${bare.slice('turn:'.length)}`;
    if (!urls.includes(stun)) urls.push(stun);
  }
  // `IceServer` is the wire shape of GET /turn/credentials, where credentials
  // are always present; a STUN entry has none, and WebRTC ignores username and
  // credential for `stun:` URLs. Empty strings keep the client honest
  // about that rather than loosening the server's contract.
  return urls.length > 0 ? [{ urls, username: '', credential: '' }] : [];
}
